<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n, I18nT } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import globalConstants from 'dashboard/constants/globals.js';
import {
  setupFacebookSdk,
  initWhatsAppEmbeddedSignup,
  createMessageHandler,
  isValidBusinessData,
} from 'dashboard/routes/dashboard/settings/inbox/channels/whatsapp/utils';

import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import ViewWrapper from './ViewWrapper.vue';

const emit = defineEmits(['use-manual-setup']);

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const isProcessing = ref(false);
const processingMessage = ref('');
const authCodeReceived = ref(false);
const authCode = ref(null);
const businessData = ref(null);
const isAuthenticating = ref(false);

const benefits = computed(() => [
  {
    key: 'EASY_SETUP',
    text: t(
      'WGPT_ONBOARDING.CHANNELS.WHATSAPP.EMBEDDED_SIGNUP.BENEFITS.EASY_SETUP'
    ),
  },
  {
    key: 'SECURE_AUTH',
    text: t(
      'WGPT_ONBOARDING.CHANNELS.WHATSAPP.EMBEDDED_SIGNUP.BENEFITS.SECURE_AUTH'
    ),
  },
  {
    key: 'AUTO_CONFIG',
    text: t(
      'WGPT_ONBOARDING.CHANNELS.WHATSAPP.EMBEDDED_SIGNUP.BENEFITS.AUTO_CONFIG'
    ),
  },
]);

const showLoader = computed(() => isAuthenticating.value || isProcessing.value);

const handleSignupError = data => {
  isProcessing.value = false;
  authCodeReceived.value = false;
  isAuthenticating.value = false;
  useAlert(
    data.error || data.message || t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE')
  );
};

const handleSignupCancellation = () => {
  isProcessing.value = false;
  authCodeReceived.value = false;
  isAuthenticating.value = false;
};

const completeSignupFlow = async businessDataParam => {
  if (!authCodeReceived.value || !authCode.value) {
    handleSignupError({
      error: t(
        'WGPT_ONBOARDING.CHANNELS.WHATSAPP.EMBEDDED_SIGNUP.AUTH_NOT_COMPLETED'
      ),
    });
    return;
  }

  isProcessing.value = true;
  processingMessage.value = t(
    'WGPT_ONBOARDING.CHANNELS.WHATSAPP.EMBEDDED_SIGNUP.PROCESSING'
  );

  try {
    await store.dispatch('inboxes/createWhatsAppEmbeddedSignup', {
      code: authCode.value,
      business_id: businessDataParam.business_id,
      waba_id: businessDataParam.waba_id,
      phone_number_id: businessDataParam?.phone_number_id || '',
    });
    authCode.value = null;
    isProcessing.value = false;
    isAuthenticating.value = false;
    router.push({ name: 'wgpt_onboarding_channels' });
  } catch (error) {
    handleSignupError({
      error:
        parseAPIErrorResponse(error) ||
        t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE'),
    });
  }
};

const handleEmbeddedSignupData = async data => {
  if (
    data.event === 'FINISH' ||
    data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
  ) {
    const businessDataLocal = data.data;
    if (isValidBusinessData(businessDataLocal)) {
      businessData.value = businessDataLocal;
      if (authCodeReceived.value && authCode.value) {
        await completeSignupFlow(businessDataLocal);
      } else {
        processingMessage.value = t(
          'WGPT_ONBOARDING.CHANNELS.WHATSAPP.EMBEDDED_SIGNUP.WAITING_FOR_AUTH'
        );
      }
    } else {
      handleSignupError({
        error: t(
          'WGPT_ONBOARDING.CHANNELS.WHATSAPP.EMBEDDED_SIGNUP.INVALID_BUSINESS_DATA'
        ),
      });
    }
  } else if (data.event === 'CANCEL') {
    handleSignupCancellation();
  } else if (data.event === 'error') {
    handleSignupError({
      error:
        data.error_message ||
        t('WGPT_ONBOARDING.CHANNELS.WHATSAPP.EMBEDDED_SIGNUP.SIGNUP_ERROR'),
    });
  }
};

const handleSignupMessage = createMessageHandler(handleEmbeddedSignupData);

const launchEmbeddedSignup = async () => {
  try {
    isAuthenticating.value = true;
    processingMessage.value = t(
      'WGPT_ONBOARDING.CHANNELS.WHATSAPP.EMBEDDED_SIGNUP.AUTH_PROCESSING'
    );

    await setupFacebookSdk(
      window.chatwootConfig?.whatsappAppId,
      window.chatwootConfig?.whatsappApiVersion
    );

    const code = await initWhatsAppEmbeddedSignup(
      window.chatwootConfig?.whatsappConfigurationId
    );

    authCode.value = code;
    authCodeReceived.value = true;
    processingMessage.value = t(
      'WGPT_ONBOARDING.CHANNELS.WHATSAPP.EMBEDDED_SIGNUP.WAITING_FOR_BUSINESS_INFO'
    );

    if (businessData.value) {
      completeSignupFlow(businessData.value);
    }
  } catch (error) {
    if (error.message === 'Login cancelled') {
      isProcessing.value = false;
      isAuthenticating.value = false;
      useAlert(
        t('WGPT_ONBOARDING.CHANNELS.WHATSAPP.EMBEDDED_SIGNUP.CANCELLED')
      );
    } else {
      handleSignupError({
        error:
          error.message ||
          t('WGPT_ONBOARDING.CHANNELS.WHATSAPP.EMBEDDED_SIGNUP.SDK_LOAD_ERROR'),
      });
    }
  }
};

onMounted(() => window.addEventListener('message', handleSignupMessage));
onBeforeUnmount(() =>
  window.removeEventListener('message', handleSignupMessage)
);
</script>

<template>
  <ViewWrapper>
    <LoadingState
      v-if="showLoader"
      :message="processingMessage"
      class="px-0 !justify-start"
    />

    <div v-else class="flex flex-col gap-6">
      <div class="flex flex-col gap-2">
        <div
          v-for="benefit in benefits"
          :key="benefit.key"
          class="flex gap-2 items-center text-sm text-n-slate-11"
        >
          <Icon icon="i-lucide-check" class="text-n-slate-11 size-4" />
          {{ benefit.text }}
        </div>
      </div>

      <I18nT
        keypath="WGPT_ONBOARDING.CHANNELS.WHATSAPP.EMBEDDED_SIGNUP.LEARN_MORE.TEXT"
        tag="span"
        class="text-sm text-n-slate-11"
      >
        <template #link>
          <a
            :href="globalConstants.WHATSAPP_EMBEDDED_SIGNUP_DOCS_URL"
            target="_blank"
            rel="noopener noreferrer"
            class="underline text-n-brand"
          >
            {{
              $t(
                'WGPT_ONBOARDING.CHANNELS.WHATSAPP.EMBEDDED_SIGNUP.LEARN_MORE.LINK_TEXT'
              )
            }}
          </a>
        </template>
      </I18nT>

      <I18nT
        keypath="WGPT_ONBOARDING.CHANNELS.WHATSAPP.EMBEDDED_SIGNUP.MANUAL_FALLBACK"
        tag="p"
        class="text-sm text-n-slate-11"
      >
        <template #link>
          <a
            href="#"
            class="underline text-n-brand"
            @click.prevent="emit('use-manual-setup')"
          >
            {{
              $t(
                'WGPT_ONBOARDING.CHANNELS.WHATSAPP.EMBEDDED_SIGNUP.MANUAL_LINK_TEXT'
              )
            }}
          </a>
        </template>
      </I18nT>
    </div>

    <template #actions>
      <Button
        :label="t('WGPT_ONBOARDING.CHANNELS.BACK')"
        variant="link"
        color="slate"
        size="sm"
        @click="router.push({ name: 'wgpt_onboarding_channels' })"
      />
      <Button
        lg
        :label="
          t('WGPT_ONBOARDING.CHANNELS.WHATSAPP.EMBEDDED_SIGNUP.SUBMIT_BUTTON')
        "
        :is-loading="showLoader"
        :disabled="showLoader"
        @click="launchEmbeddedSignup"
      />
    </template>
  </ViewWrapper>
</template>
